// src/services/filterLocations.js
//
// Pure filter utility — no React, no side-effects, no storage reads.
// Extracted from PortfolioView.jsx (P6) so LocationDetail (caption) and
// future consumers can apply the same pipeline without importing the view.
//
// applyStageGate       — stage gate (also used for secondary counts)
// applyAllFilters      — full filter pipeline
// applyAllFiltersAndSort — combined helper for the LocationDetail caption
//                          and (future, L3) Next/Previous nav. Filter + sort
//                          must be applied as a pair before any indexOf —
//                          see Lesson 48 rationale in tasks/lessons.md.

import { getActiveLeaseTypes } from "../utils/leases.js";
import { applySort, DEFAULT_SORT_KEY } from "../utils/sortLocations.js";
import { DEFAULT_EXCLUDED_STAGES as _EXCLUDED_STAGES_ARRAY, STAGES } from "../app/tokens.js";
import { getMAndATagName } from "../utils/mAndA.js";
import { foldText } from "../utils/textSearch.js";
import { matchIdentity } from "../utils/curatedSearch.js";

// LSE-VACATED (Option C revert): vacated is treated symmetrically with closed
// under the "Inactive" grouping — both are excluded from the default "All"
// view and are reachable only via explicit filter selection. Earlier in the
// slice we tried Option B (include vacated in All); the symmetry with Retired
// is the cleaner semantic so we reverted. See PortfolioView's activeCount
// comment for the full rationale.
// Task-238: promoted to tokens.js so filter and drift-guard test share one
// source of truth.
// DORMANT intentionally absent from DEFAULT_EXCLUDED_STAGES: a dormant office
// is temporarily closed with reopening imminent — it belongs in the Active
// portfolio group and remains visible in the "All Active" view.
const DEFAULT_EXCLUDED_STAGES = new Set(_EXCLUDED_STAGES_ARRAY);

// Apply stage gate (used by both filter pipeline and the secondary-filter
// count derivation).
// LSE-LOCATIONS-STAGE-DROPDOWN-RESTRUCTURE:
//   "all_stages" — "show everything" meta-value (no stage gate at all).
//   "all" (All Active) — excludes Inactive stages (closed + vacated) and the
//     now-retired pipeline stages (negotiating + prospecting) via
//     DEFAULT_EXCLUDED_STAGES. Active = Operational + Dormant + In-Build.
//   "all_inactive" branch removed; falls through to exact-stage match (returns 0
//     for any persisted value — acceptable, user can clear the filter).
// LS7: showClosed removed. stage="all" always excludes closed rows; stage="closed"
// returns only closed locations.
// LSE-VACATED: stage="all" also excludes vacated rows (symmetric with closed
// under the "Inactive" grouping); stage="vacated" returns only vacated rows.
// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: negotiating/prospecting now
// in DEFAULT_EXCLUDED_STAGES so legacy rows are hidden from the default view.
export function applyStageGate(locations, { stage }) {
  if (stage === "all_stages") {
    // all_stages: show every location regardless of stage.
    return locations;
  }
  if (stage === "draft") return locations.filter((s) => s.stage === "draft");
  if (stage === "closed") {
    return locations.filter((s) => s.stage === "closed");
  }
  if (stage === "vacated") {
    return locations.filter((s) => s.stage === "vacated");
  }
  // Exclude Inactive stages (closed + vacated) for all remaining cases.
  const result = locations.filter((s) => !DEFAULT_EXCLUDED_STAGES.has(s.stage));
  if (stage === "all") {
    // All Active: Operational + Dormant + In-Build.
    return result;
  }
  // Specific stage: exact match.
  return result.filter((s) => s.stage === stage);
}

export function applyAllFilters(locations, filters) {
  let result = applyStageGate(locations, filters);
  // P3 — Health is filtered alongside the other secondary dimensions. When a
  // specific health value is selected, locations with `health == null` are
  // excluded (they only surface under "All", consistent with the brief). When
  // health === "all", null-health rows pass through unchanged.
  if (filters.health && filters.health !== "all") {
    if (filters.health === "off_track") {
      result = result.filter((s) => s.health === "yellow" || s.health === "red");
    } else {
      result = result.filter((s) => s.health === filters.health);
    }
  }
  if (filters.region !== "all") {
    result = result.filter((s) => s.region === filters.region);
  }
  // MA2 — special meta-values "any" (has any tag) and "none" (undesignated)
  // must precede the canonical-id equality fallthrough (L7 order guard).
  if (filters.mAndA === "any") {
    result = result.filter((s) => s.mAndA != null);
  } else if (filters.mAndA === "none") {
    result = result.filter((s) => s.mAndA == null);
  } else if (filters.mAndA !== "all") {
    result = result.filter((s) => s.mAndA === filters.mAndA);
  }
  if (filters.leaseType !== "all") {
    // LSE-LOCATIONS-FILTER-MULTI-LEASE: include location if ANY of its active
    // lease types matches the selected filter value.
    result = result.filter((s) => getActiveLeaseTypes(s).includes(filters.leaseType));
  }
  return result;
}

export function applyAllFiltersAndSort(locations, filters, sortKey = DEFAULT_SORT_KEY) {
  return applySort(applyAllFilters(locations, filters), sortKey);
}

// LS3 — All filter dimensions now scope the stat bar (Stage, Region, Lease,
// M&A, Health). The Closed toggle continues to affect math but not tint.
// applyScopeFilters delegates to applyAllFilters — they produce identical
// results and the named export is kept for import stability.
export function applyScopeFilters(locations, filters) {
  return applyAllFilters(locations, filters);
}

// LOCATION-PAGER-RESPECT-SEARCH — Single source of truth for the search
// predicate used by both PortfolioView (list display) and deriveNavLocations
// (pager traversal). Case-insensitive, diacritic-insensitive substring across
// the key searchable fields: market, locationAlias, region, stage label, M&A
// tag label, district. Returns the array unchanged when searchTerm is
// empty/whitespace-only.
// SEARCH-SCOPE-STANDARDIZE — exported so Leases and Projects can share the
// same NFD diacritic-stripping normalizer instead of duplicating the logic.
// Compatibility export for existing consumers and test mocks. New callers
// should import the shared utility directly.
export { foldText as fold };

export function applySearchFilter(locations, searchTerm) {
  const q = (searchTerm || "").trim();
  if (!q) return locations;
  return locations.filter((loc) => matchOfficeIdentity(loc, q).eligible);
}

export function matchOfficeIdentity(loc, query) {
  const stageLabel = STAGES[loc.stage]?.label || loc.stage;
  return matchIdentity(query, [
    { value: loc.market ? `${loc.market}${loc.locationAlias ? ` · ${loc.locationAlias}` : ""}` : null,
      primary: true, visible: true },
    ...[loc.market, loc.locationAlias].map((value) => ({ value, primary: true, visible: true })),
    ...[loc.id, loc.address, loc.name, loc.buildingName, loc.propertyName].map((value) => ({ value, primary: true })),
    { value: loc.suite },
    { value: loc.region, visible: true },
    { value: stageLabel, visible: true },
    { value: loc.stage, displayValue: stageLabel },
    { value: loc.mAndA ? getMAndATagName(loc.mAndA) : null, visible: true },
    { value: loc.district },
  ]);
}
